import { t } from '../../../../../shared/i18n';
// 域名搜索页的纯函数工具（无 React 依赖）

export const normalizeTld = (value = '') => String(value).trim().replace(/^\.+/, '').toLowerCase();

/**
 * 解析输入："yeap" → {label:'yeap'}；"yeap.com" / "https://www.yeap.com/x" → {label:'yeap', tld:'com'}
 * 已知多级后缀（com.cn）按目录匹配最长后缀。
 */
export const parseDomainInput = (raw, knownTlds) => {
    let s = String(raw || '').trim().toLowerCase();
    s = s.replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/^www\./, '').replace(/\s+/g, '');
    s = s.replace(/。|．|｡/g, '.');
    if (!s) return null;
    const parts = s.split('.').filter(Boolean);
    if (parts.length < 2) return { label: parts[0] || s, tld: null };
    // 最长已知后缀优先（com.cn、中国）
    for (let i = 1; i < parts.length; i += 1) {
        const cand = parts.slice(i).join('.');
        if (!knownTlds || knownTlds.has(cand)) {
            return { label: parts.slice(0, i).join('.'), tld: cand };
        }
    }
    return { label: parts.slice(0, -1).join('.'), tld: parts[parts.length - 1] };
};

/** 合法性：字母数字/连字符/非 ASCII，1–63，首尾非连字符 */
export const isValidLabel = (label) => {
    if (!label || label.length > 63) return false;
    if (/^-|-$/.test(label)) return false;
    if (label.includes('.')) return false;
    // eslint-disable-next-line no-control-regex
    return /^[a-z0-9-]+$/.test(label) || /[^\x00-\x7f]/.test(label);
};

/**
 * 检测标签的语种桶（与后端 IdnService 桶 key 一致）。ASCII → null。
 */
export const detectLanguage = (label = '') => {
    // eslint-disable-next-line no-control-regex
    if (!/[^\x00-\x7f]/.test(label)) return null;
    if (/[぀-ヿ]/.test(label)) return 'ja';
    if (/[가-힯ᄀ-ᇿ]/.test(label)) return 'ko';
    if (/[一-鿿㐀-䶿]/.test(label)) return 'zh';
    if (/[Ѐ-ӿ]/.test(label)) return 'cyrl';
    if (/[؀-ۿݐ-ݿ]/.test(label)) return 'arab';
    if (/[Ͱ-Ͽ]/.test(label)) return 'grek';
    if (/[֐-׿]/.test(label)) return 'hebr';
    if (/[฀-๿]/.test(label)) return 'thai';
    if (/[ऀ-ॿ]/.test(label)) return 'deva';
    if (/[஀-௿]/.test(label)) return 'taml';
    if (/[À-ɏ]/.test(label)) return 'latn';
    return null;
};

export const PRICE_RANGES = [
    { value: 'all', label: '不限价格' },
    { value: '10', label: '首年 ≤ 10', max: 10 },
    { value: '30', label: '首年 ≤ 30', max: 30 },
    { value: '100', label: '首年 ≤ 100', max: 100 },
];

/**
 * 按筛选条件过滤目录 TLD 列表（返回已排序数组：热门优先，再按首年价）。
 * filters: { category, price, language, idnOnly }
 */
export const filterTlds = (catalog, filters = {}) => {
    if (!catalog) return [];
    const { category = 'popular', price = 'all', language = null, idnOnly = false } = filters;
    let list = catalog.tlds || [];

    if (category && category !== 'all') {
        const cat = catalog.categories?.[category];
        const set = new Set((cat?.items || []).map((i) => i.tld));
        list = list.filter((t) => set.has(t.tld));
    }
    if (language) {
        const lang = catalog.languages?.[language];
        const set = new Set([...(lang?.items || []), ...(lang?.native || [])]);
        list = list.filter((t) => set.has(t.tld));
    }
    if (idnOnly) list = list.filter((t) => t.is_idn);
    const range = PRICE_RANGES.find((r) => r.value === price);
    if (range?.max) list = list.filter((t) => t.registration_price != null && Number(t.registration_price) <= range.max);

    const rank = new Map((catalog.featured || []).map((t, i) => [t.tld, i]));
    return [...list].sort((a, b) => {
        // 暂未开放（强制注册商不售）的后缀排最后
        const ua = a.available === false ? 1 : 0;
        const ub = b.available === false ? 1 : 0;
        if (ua !== ub) return ua - ub;
        const ra = rank.has(a.tld) ? rank.get(a.tld) : 1e6;
        const rb = rank.has(b.tld) ? rank.get(b.tld) : 1e6;
        if (ra !== rb) return ra - rb;
        // 本语种的原生后缀（.中国）排在接受该语种标签的 ASCII 后缀前
        if (language) {
            const na = a.is_idn ? 0 : 1;
            const nb = b.is_idn ? 0 : 1;
            if (na !== nb) return na - nb;
        }
        return (Number(a.registration_price) || 1e9) - (Number(b.registration_price) || 1e9);
    });
};

/** 简单变体：前后缀 + 连字符，最多 n 个 */
export const labelVariations = (label, n = 6) => {
    if (!label || label.length > 40) return [];
    // eslint-disable-next-line no-control-regex
    const ascii = !/[^\x00-\x7f]/.test(label);
    const out = ascii
        ? [`get${label}`, `${label}app`, `my${label}`, `${label}hq`, `try${label}`, `${label}online`, `the${label}`, `${label}-cloud`]
        : [`${label}网`, `${label}在线`, `我的${label}`, `${label}商城`];
    return out.filter((v) => v !== label && v.length <= 63).slice(0, n);
};

/** 结果状态：available | taken | premium | unsupported | error */
export const resultState = (r) => {
    if (!r) return 'error';
    if (r.is_idn_incompatible) return 'unsupported';
    if (r.success === false) return 'error';
    if (r.available && (r.premium || r.is_premium)) return 'premium';
    if (r.available) return 'available';
    return 'taken';
};

export const resultKey = (r) => String(r?.domain || '').toLowerCase();

/** 把后端错误翻成一句短话（绝不带注册商名） */
export const friendlyError = (r) => {
    const e = String(r?.error || r?.reason || '');
    if (/not available for registration/i.test(e)) return t('Registration is not yet supported');
    if (/IDN|script/i.test(e)) return t('This extension does not support this language');
    if (/timeout|timed out/i.test(e)) return t('Query timed out. Please try again.');
    return e ? t('Query failed') : t('Currently unavailable for registration');
};

export const toCsv = (rows) => {
    const head = [t('Domains'), t('Status'), t('First-year price'), t('Renewal price'), t('Currency')];
    const label = { available: t('Available to register'), premium: t('Available at a premium'), taken: t('Already registered'), unsupported: t('Not supported'), error: t('Query failed') };
    const body = rows.map((r) => [
        r.domain_unicode || r.domain,
        label[resultState(r)],
        r.pricing?.register ?? '',
        r.pricing?.renew ?? '',
        r.pricing?.currency ?? '',
    ]);
    return '﻿' + [head, ...body].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
};

export const downloadText = (content, filename, mime = 'text/csv;charset=utf-8') => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
